/**
 * Authentication Context
 * Manages user auth state, tenant info, and login/logout across the app
 */

import React, { createContext, useState, useEffect, useCallback } from 'react';
import { LoginResponse, MfaChallengeRequiredError, MfaFactor, User, UserRole } from '../api/types';
import { createAPIClient, IAuthApi, IAuthTokenApi } from '../api';
import { decodeToken, isTokenValid } from '../utils/jwt';
import * as storage from '../utils/storage';

export interface AuthContextType {
  // State
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  user: User | null;
  tenantId: string | null;
  tenantSlug: string | null;
  token: string | null;

  // Actions
  /**
   * Resolves with the signed-in user (LoginPage picks the landing page from the role).
   * With MFA on (tracker item 63) it rejects with MfaChallengeRequiredError and
   * `mfaChallenge` is set: finish with completeMfaLogin.
   */
  login: (tenantSlug: string, email: string, password: string) => Promise<User>;
  /** A sign-in waiting for its authenticator code (tracker item 63), or null. */
  mfaChallenge: { expiresAt: string } | null;
  /** Second step: a code or a recovery code for the pending challenge. */
  completeMfaLogin: (factor: MfaFactor) => Promise<User>;
  /** Drop the pending challenge (back to the password form). */
  cancelMfaLogin: () => void;
  /** After enrolling: switch to the fresh token the backend issued and lift the enrollment gate. */
  markMfaEnrolled: (token: string) => void;
  logout: () => Promise<void>;
  clearError: () => void;
  /** Clear the must-change-password gate after a successful self-service change. */
  markPasswordChanged: () => void;
}

export const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  // State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantSlug, setTenantSlug] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  // API client (stable reference)
  const apiClient = React.useMemo<IAuthApi & IAuthTokenApi>(() => createAPIClient(), []);

  // Initialize: check if user is already logged in (token in localStorage)
  useEffect(() => {
    const initAuth = async () => {
      try {
        const savedToken = storage.getAuthToken();

        if (savedToken && isTokenValid(savedToken)) {
          // Token exists and is valid, decode it
          const decoded = decodeToken(savedToken);

          if (decoded) {
            setToken(savedToken);
            setTenantId(decoded.tenantId);
            setTenantSlug(storage.getTenantSlug());

            // Build user object from token
            const restoredUser: User = {
              id: decoded.userId,
              email: decoded.email,
              name: decoded.email.split('@')[0], // Use email prefix as name
              role: decoded.role as UserRole,
              tenantId: decoded.tenantId,
              // Not in the JWT; persisted at login and by the API client's
              // 403 MUST_CHANGE_PASSWORD handler.
              mustChangePassword: storage.getMustChangePassword(),
              mfaEnrollmentRequired: storage.getMfaEnrollmentRequired(),
            };

            setUser(restoredUser);
            setIsAuthenticated(true);

            // Set token on API client for subsequent requests
            apiClient.setAuthToken(savedToken);
          }
        } else if (savedToken) {
          // Token exists but is expired, clear it
          storage.clearAuthStorage();
        }
      } catch (err) {
        console.error('Auth init error:', err);
        storage.clearAuthStorage();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [apiClient]);

  // A pending MFA challenge (tracker item 63): kept here, never in storage
  const [mfaPending, setMfaPending] = useState<{ challengeToken: string; expiresAt: string; tenantSlug: string; email: string } | null>(null);

  /** Store and apply a session from login or the MFA step. */
  const startSession = useCallback(
    (response: LoginResponse, slug: string, email: string): User => {
      storage.saveAuthToken(response.token);
      storage.saveTenantId(response.user.tenantId);
      storage.saveTenantSlug(slug);
      storage.saveUserEmail(email);
      storage.saveMustChangePassword(response.user.mustChangePassword === true);
      storage.saveMfaEnrollmentRequired(response.user.mfaEnrollmentRequired === true);

      setToken(response.token);
      setTenantId(response.user.tenantId);
      setTenantSlug(slug);
      const signedIn: User = {
        ...response.user,
        mustChangePassword: response.user.mustChangePassword === true,
        mfaEnrollmentRequired: response.user.mfaEnrollmentRequired === true,
      };
      setUser(signedIn);
      setIsAuthenticated(true);
      apiClient.setAuthToken(response.token);
      return signedIn;
    },
    [apiClient]
  );

  // Login
  const login = useCallback(
    async (tenantSlug: string, email: string, password: string) => {
      setIsLoading(true);
      setError(null);
      setMfaPending(null);

      try {
        const response = await apiClient.login(tenantSlug, email, password);
        return startSession(response, tenantSlug, email);
      } catch (err) {
        if (err instanceof MfaChallengeRequiredError) {
          setMfaPending({ challengeToken: err.challengeToken, expiresAt: err.expiresAt, tenantSlug, email });
          throw err;
        }
        const errorMessage = err instanceof Error ? err.message : 'Login failed';
        setError(errorMessage);
        setIsAuthenticated(false);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [apiClient, startSession]
  );

  const completeMfaLogin = useCallback(
    async (factor: MfaFactor) => {
      if (!mfaPending) throw new Error('Sign in again');
      setIsLoading(true);
      try {
        const response = await apiClient.verifyMfaLogin(mfaPending.challengeToken, factor);
        setMfaPending(null);
        return startSession(response, mfaPending.tenantSlug, mfaPending.email);
      } finally {
        setIsLoading(false);
      }
    },
    [apiClient, mfaPending, startSession]
  );

  const cancelMfaLogin = useCallback(() => setMfaPending(null), []);

  const markMfaEnrolled = useCallback(
    (freshToken: string) => {
      storage.saveAuthToken(freshToken);
      storage.saveMfaEnrollmentRequired(false);
      apiClient.setAuthToken(freshToken);
      setToken(freshToken);
      setUser((prev) => (prev ? { ...prev, mfaEnrollmentRequired: false } : prev));
    },
    [apiClient]
  );

  // Logout
  const logout = useCallback(async () => {
    setIsLoading(true);

    try {
      // Call logout endpoint
      await apiClient.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Clear everything
      storage.clearAuthStorage();
      apiClient.clearAuthToken();

      setToken(null);
      setMfaPending(null);
      setTenantId(null);
      setTenantSlug(null);
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
      setIsLoading(false);
    }
  }, [apiClient]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const markPasswordChanged = useCallback(() => {
    storage.saveMustChangePassword(false);
    setUser((prev) => (prev ? { ...prev, mustChangePassword: false } : prev));
  }, []);

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    error,
    user,
    tenantId,
    tenantSlug,
    token,
    login,
    mfaChallenge: mfaPending ? { expiresAt: mfaPending.expiresAt } : null,
    completeMfaLogin,
    cancelMfaLogin,
    markMfaEnrolled,
    logout,
    clearError,
    markPasswordChanged,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to use auth context
 */
export function useAuth(): AuthContextType {
  const context = React.useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
}
